import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Receipt, Image, Mail, LogOut, Download, Eye, MousePointer, ArrowUpRight, Sparkles, CalendarClock, BadgeCheck, Plus, Edit3,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, BarChart, Bar } from "recharts";
import { api, formatGBP, formatNumber, formatDate, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Crest, Pill, Modal, Field, TextInput, PrimaryButton, GhostButton, FooterStrip, TenantThemer } from "../../components/shell";
import PhoneMockup from "../../components/PhoneMockup";
import { PLACEMENTS, PLATFORMS } from "../../components/DigitalAdsForm";

export default function SponsorPortal() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [offerModal, setOfferModal] = useState(null);

    const load = async () => {
        try {
            setErr("");
            const { data: d } = await api.get("/sponsor-portal");
            setData(d);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    useEffect(() => { load(); }, []);

    const onLogout = async () => { await logout(); navigate("/login"); };

    const onSaveOffer = async (draft) => {
        if (offerModal.id) await api.patch(`/sponsor-portal/offers/${offerModal.id}`, draft);
        else await api.post(`/sponsor-portal/offers`, draft);
        setOfferModal(null);
        await load();
    };

    if (err) return (
        <div className="min-h-screen bg-[var(--sb-page)] text-white flex items-center justify-center p-12">
            <div className="border border-rose-500/40 text-rose-300 p-8 max-w-md text-sm">{err}</div>
        </div>
    );
    if (!data) return (
        <div className="min-h-screen bg-[var(--sb-page)] text-white flex items-center justify-center text-xs uppercase tracking-ultra-wide text-zinc-500">
            <span className="kpi-pip animate-pulse mr-3" style={{ background: "var(--sb-amber)" }} /> loading your portal
        </div>
    );

    const { tenant, sponsor, package: pkg, invoices, next_invoice, impressions_timeline, offers, artwork, digital_ads, digital_ads_source, digital_ads_stats, live_analytics } = data;
    const totalImpressions = (impressions_timeline || []).reduce((a, b) => a + b.impressions, 0);
    const totalClicks = (impressions_timeline || []).reduce((a, b) => a + b.clicks, 0);

    return (
        <div data-testid="sponsor-portal" className="min-h-screen text-white bg-[var(--sb-page)]">
            <TenantThemer tenant={tenant} />
            <header className="border-b hairline sticky top-0 backdrop-blur-xl bg-[rgba(5,5,5,0.85)] z-40">
                <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <Crest tenant={tenant} size={36} />
                        <div className="min-w-0">
                            <div className="font-display font-bold text-sm leading-tight truncate">{tenant?.name}</div>
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 truncate">Sponsor portal · {sponsor?.name}</div>
                        </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-3">
                        <button className="border hairline px-3 py-2 text-xs hover:bg-white/5 inline-flex items-center gap-2" data-testid="portal-message-club">
                            <Mail size={13} /> Message the club
                        </button>
                        <button onClick={onLogout} className="text-zinc-500 hover:text-white p-2" data-testid="portal-logout" aria-label="Sign out">
                            <LogOut size={15} />
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 md:px-10 py-8 md:py-12 space-y-10">
                <section className="grid grid-cols-1 lg:grid-cols-12 gap-3" data-testid="hero-deal">
                    <div className="lg:col-span-8 border hairline p-7 md:p-10 relative overflow-hidden">
                        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: "var(--tenant-primary)" }} />
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3">Your sponsorship · {tenant?.season}</div>
                        <h1 className="font-sport text-4xl md:text-5xl leading-[0.95] uppercase" data-testid="portal-title">
                            Welcome back,<br /><span style={{ color: "var(--tenant-primary)" }}>{sponsor?.name}.</span>
                        </h1>
                        <p className="font-editorial italic text-zinc-300 mt-4 text-lg max-w-md">
                            You're the <span className="not-italic uppercase tracking-ultra-wide text-xs border px-2 py-0.5 hairline ml-1 align-middle">{sponsor?.tier}</span>{" "}
                            partner this season. Here's how the deal is performing.
                        </p>
                        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
                            <Stat label="Tier" value={sponsor?.tier} />
                            <Stat label="Monthly" value={formatGBP(sponsor?.monthly_gbp)} />
                            <Stat label="Sponsoring since" value={formatDate(sponsor?.start_date)} />
                            <Stat label="Renewal" value={formatDate(sponsor?.renewal_date)} />
                        </div>
                    </div>

                    <aside className="lg:col-span-4 border hairline p-7 flex flex-col justify-between" data-testid="next-invoice-card">
                        <div>
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3">Next invoice</div>
                            {next_invoice ? (
                                <>
                                    <div className="font-mono text-xs text-zinc-400">{next_invoice.id}</div>
                                    <div className="font-display font-extrabold text-4xl tracking-tightest mt-3">{formatGBP(next_invoice.amount_gbp)}</div>
                                    <div className="text-sm text-zinc-400 mt-2 inline-flex items-center gap-2"><CalendarClock size={13} /> {formatDate(next_invoice.issued_at)}</div>
                                </>
                            ) : (
                                <div className="text-sm text-zinc-500 italic">No invoice outstanding. You're up to date.</div>
                            )}
                        </div>
                        {next_invoice && (
                            <PrimaryButton tenantTheme data-testid="pay-invoice-cta">Pay now <ArrowUpRight size={14} /></PrimaryButton>
                        )}
                    </aside>
                </section>

                {/* LIVE FAN-APP ANALYTICS (Firestore) */}
                {live_analytics && (
                    <section className="border hairline p-7 md:p-10 relative overflow-hidden" data-testid="live-analytics-section">
                        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-[0.06] blur-3xl" style={{ background: "var(--tenant-primary)" }} />
                        <div className="relative">
                            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                                <div>
                                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1 inline-flex items-center gap-2">
                                        <span className="kpi-pip animate-pulse" style={{ background: "#10B981" }} /> Live · fan app
                                    </div>
                                    <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">Real fans, real engagement</h2>
                                    <p className="font-editorial italic text-zinc-400 mt-2 text-base">Synced directly from the {tenant?.short_name || tenant?.name} fan app.</p>
                                </div>
                                <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-600 font-mono">id · {live_analytics.sponsor_id}</div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                                <KPI label="Page visits" value={formatNumber(live_analytics.total_visits)} testId="live-visits" />
                                <KPI label="Unique visitors" value={formatNumber(live_analytics.unique_visitors)} testId="live-unique" />
                                <KPI label="Offer clicks" value={formatNumber(live_analytics.total_offer_clicks)} testId="live-offer-clicks" />
                                <KPI label="CTA taps" value={formatNumber(live_analytics.total_cta_taps)} testId="live-cta-taps" />
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                                {/* Per-offer chart */}
                                <div className="lg:col-span-7 border hairline p-5">
                                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-4">Clicks per offer</div>
                                    {(live_analytics.chart || []).length === 0 ? (
                                        <div className="text-xs text-zinc-500 py-12 text-center">No offer activity yet.</div>
                                    ) : (
                                        <div className="h-56 -mx-2">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={live_analytics.chart} layout="vertical" margin={{ left: 8, right: 8 }}>
                                                    <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
                                                    <XAxis type="number" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
                                                    <YAxis type="category" dataKey="label" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} width={140} />
                                                    <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 0, fontSize: 12 }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                                                    <Bar dataKey="clicks" fill="var(--tenant-primary)" radius={[0, 0, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </div>
                                {/* CTA breakdown */}
                                <div className="lg:col-span-5 border hairline p-5">
                                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-4">CTA breakdown</div>
                                    <div className="space-y-3">
                                        <CtaRow label="Phone calls" value={live_analytics.cta?.phone || 0} total={live_analytics.total_cta_taps} testId="live-cta-phone" />
                                        <CtaRow label="App opens" value={live_analytics.cta?.app || 0} total={live_analytics.total_cta_taps} testId="live-cta-app" />
                                        <CtaRow label="Website visits" value={live_analytics.cta?.website || 0} total={live_analytics.total_cta_taps} testId="live-cta-website" />
                                    </div>
                                </div>
                            </div>

                            {/* Active vs inactive offers list */}
                            {(live_analytics.offers_active.length + live_analytics.offers_inactive.length) > 0 && (
                                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <OffersBox title={`Active offers · ${live_analytics.offers_active.length}`} offers={live_analytics.offers_active} tone="active" />
                                    <OffersBox title={`Inactive offers · ${live_analytics.offers_inactive.length}`} offers={live_analytics.offers_inactive} tone="inactive" />
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* IMPRESSIONS */}
                <section className="border hairline p-7" data-testid="impressions-card">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                        <div>
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Performance · last 30 days</div>
                            <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">Fans saw your brand</h2>
                        </div>
                        <div className="flex items-center gap-8">
                            <Tile icon={<Eye size={11} />} label="Impressions" value={formatNumber(totalImpressions)} />
                            <Tile icon={<MousePointer size={11} />} label="Clicks" value={formatNumber(totalClicks)} />
                        </div>
                    </div>
                    <div className="h-56 -mx-2">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={impressions_timeline || []}>
                                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="day" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
                                <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} width={40} />
                                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 0, fontSize: 12 }} cursor={{ stroke: "rgba(255,255,255,0.15)" }} />
                                <Line type="monotone" dataKey="impressions" stroke="var(--tenant-primary)" strokeWidth={2} dot={{ fill: "var(--tenant-primary)", r: 3 }} />
                                <Line type="monotone" dataKey="clicks" stroke="#FFFFFF" strokeWidth={1.4} dot={false} strokeDasharray="3 3" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </section>

                {/* DIGITAL APP ADS */}
                {digital_ads && (
                    <section className="border hairline p-7 md:p-10 relative overflow-hidden" data-testid="digital-ads-section">
                        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-[0.08] blur-3xl" style={{ background: "var(--tenant-primary)" }} />
                        <div className="relative">
                            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                                <div>
                                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">In the fan app</div>
                                    <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">Your digital placements</h2>
                                    <p className="font-editorial italic text-zinc-400 mt-2 text-base">Right where the fans live — on their phones every match week.</p>
                                </div>
                                {digital_ads_source === "sponsor" && (
                                    <span className="text-[10px] uppercase tracking-ultra-wide px-2 py-1 border" style={{ borderColor: "var(--sb-amber)", color: "var(--sb-amber)" }}>
                                        Bespoke for you
                                    </span>
                                )}
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                                <div className="lg:col-span-7">
                                    <PhoneMockup ads={digital_ads} sponsorName={sponsor?.name} />
                                </div>

                                <div className="lg:col-span-5 space-y-4">
                                    {/* Placement chips */}
                                    <div>
                                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-2">What you've got</div>
                                        <div className="space-y-1.5">
                                            {(digital_ads.placements || []).map((k) => {
                                                const p = PLACEMENTS.find((x) => x.key === k);
                                                if (!p) return null;
                                                return (
                                                    <div key={k} className="flex items-start gap-2.5 border hairline px-3 py-2" data-testid={`ads-placement-${k}`}>
                                                        <span className="kpi-pip mt-2" style={{ background: "var(--tenant-primary)" }} />
                                                        <div>
                                                            <div className="text-sm">{p.label}</div>
                                                            <div className="text-[11px] text-zinc-500">{p.sub}</div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-2">Where it shows</div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {(digital_ads.platforms || []).map((k) => {
                                                const p = PLATFORMS.find((x) => x.key === k);
                                                if (!p) return null;
                                                const Icon = p.Icon;
                                                return (
                                                    <span key={k} className="inline-flex items-center gap-1.5 border hairline px-2.5 py-1 text-xs" data-testid={`ads-platform-${k}`}>
                                                        <Icon size={11} /> {p.label}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-2">Campaign rule</div>
                                        <div className="text-sm text-zinc-300">
                                            {digital_ads.rule_type === "none" && "Always-on while you're an active sponsor."}
                                            {digital_ads.rule_type === "period" && `${formatDate(digital_ads.period_start)} → ${formatDate(digital_ads.period_end)}`}
                                            {digital_ads.rule_type === "impressions" && `Up to ${(digital_ads.impressions_cap || 0).toLocaleString()} impressions`}
                                            {digital_ads.rule_type === "clicks" && `Up to ${(digital_ads.clicks_cap || 0).toLocaleString()} clicks`}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Stats strip */}
                            {digital_ads_stats && (
                                <div className="mt-8 pt-6 border-t hairline">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Estimated this period</div>
                                        <span className="text-[10px] text-zinc-600 italic">{digital_ads_stats.sync_note}</span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3">
                                        <Tile icon={<Eye size={11} />} label="Impressions" value={formatNumber(digital_ads_stats.impressions_delivered)} />
                                        <Tile icon={<MousePointer size={11} />} label="Clicks" value={formatNumber(digital_ads_stats.clicks_delivered)} />
                                        <Tile icon={<Sparkles size={11} />} label="CTR" value={`${digital_ads_stats.ctr_pct}%`} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* OFFERS */}
                <section data-testid="offers-section">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Offers</div>
                            <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">Live in the fan app</h2>
                        </div>
                        <PrimaryButton tenantTheme onClick={() => setOfferModal({ id: null, title: "", active: true })} data-testid="create-offer-cta">
                            <Plus size={14} strokeWidth={2.4} /> New offer
                        </PrimaryButton>
                    </div>
                    {offers.length === 0 ? (
                        <div className="border hairline p-12 text-center text-sm text-zinc-500">No offers yet — create one to surface in the fan app.</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {offers.map((o) => (
                                <div key={o.id} className="border hairline p-6 flex flex-col justify-between min-h-[180px] hover:border-white/20 transition-colors" data-testid={`offer-${o.id}`}>
                                    <div>
                                        <div className="flex items-center justify-between">
                                            <Pill tone={o.active ? "active" : "paused"}>
                                                <span className="kpi-pip" style={{ background: o.active ? "#10B981" : "#52525B" }} />
                                                {o.active ? "Live" : "Off"}
                                            </Pill>
                                            <button onClick={() => setOfferModal({ id: o.id, title: o.title, active: o.active })} className="text-zinc-400 hover:text-white p-1" data-testid={`offer-edit-${o.id}`} aria-label="Edit offer">
                                                <Edit3 size={13} />
                                            </button>
                                        </div>
                                        <div className="font-display font-bold text-base tracking-tight mt-3 leading-snug">{o.title}</div>
                                        {o.firestore_synced && (
                                            <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-ultra-wide" style={{ color: "var(--tenant-primary)" }} data-testid={`offer-synced-${o.id}`}>
                                                <BadgeCheck size={11} /> Live in fan app
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
                                        <div className="border-t hairline pt-3">
                                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Views</div>
                                            <div className="font-display font-bold text-lg">{formatNumber(o.views)}</div>
                                        </div>
                                        <div className="border-t hairline pt-3">
                                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Clicks</div>
                                            <div className="font-display font-bold text-lg">{formatNumber(o.clicks)}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* DELIVERABLES + ARTWORK */}
                <section className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                    <div className="lg:col-span-7 border hairline p-7" data-testid="deliverables-card">
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Your package</div>
                        <h3 className="font-display font-extrabold text-2xl tracking-tight">{pkg?.name || "—"}</h3>
                        <p className="text-zinc-400 text-sm mt-2">{pkg?.description || "Your sponsor package details, when allocated, appear here."}</p>
                        {pkg?.deliverables?.length > 0 && (
                            <ul className="mt-5 space-y-2">
                                {pkg.deliverables.map((d, i) => (
                                    <li key={i} className="flex items-center gap-3 border-t hairline pt-2.5 text-sm">
                                        <BadgeCheck size={14} className="shrink-0" style={{ color: "var(--tenant-primary)" }} />
                                        <span className="text-zinc-200">{d}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="lg:col-span-5 border hairline p-7" data-testid="artwork-card">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Artwork</div>
                                <h3 className="font-display font-extrabold text-xl tracking-tight">Approved files</h3>
                            </div>
                            <Image size={16} className="text-zinc-500" aria-hidden />
                        </div>
                        {artwork.length === 0 ? (
                            <div className="text-xs text-zinc-500">No artwork uploaded yet.</div>
                        ) : (
                            <div className="space-y-2">
                                {artwork.map((a) => (
                                    <div key={a.id} className="flex items-center justify-between border-t hairline pt-3" data-testid={`artwork-${a.id}`}>
                                        <div className="min-w-0">
                                            <div className="text-sm text-white truncate font-mono">{a.name}</div>
                                            <div className="text-[11px] text-zinc-500">{a.size} · uploaded {formatDate(a.uploaded_at)}</div>
                                        </div>
                                        <button className="text-zinc-400 hover:text-white p-2" aria-label="Download" data-testid={`download-${a.id}`}>
                                            <Download size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                {/* INVOICES */}
                <section className="border hairline p-7" data-testid="invoices-mini">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Invoices</div>
                            <h3 className="font-display font-extrabold text-xl tracking-tight">Your billing history</h3>
                        </div>
                        <Receipt size={16} className="text-zinc-500" aria-hidden />
                    </div>
                    {invoices.length === 0 ? (
                        <div className="text-xs text-zinc-500">No invoices yet.</div>
                    ) : (
                        <div className="space-y-2">
                            {invoices.map((inv, i) => (
                                <div key={inv.id} className={`flex items-center justify-between text-sm py-3 ${i !== 0 ? "border-t hairline" : ""}`} data-testid={`mini-invoice-${inv.id}`}>
                                    <div className="flex items-center gap-4">
                                        <span className="font-mono text-xs text-zinc-500">{inv.id}</span>
                                        <span className="text-zinc-300 text-xs">{formatDate(inv.issued_at)}</span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <Pill tone={inv.status === "Paid" ? "paid" : "unpaid"}>{inv.status}</Pill>
                                        <span className="font-display font-bold tracking-tight w-20 text-right">{formatGBP(inv.amount_gbp)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                <FooterStrip tenant={tenant} />
            </main>

            {offerModal && (
                <OfferModal initial={offerModal} onClose={() => setOfferModal(null)} onSubmit={onSaveOffer} />
            )}
        </div>
    );
}

const Stat = ({ label, value }) => (
    <div>
        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">{label}</div>
        <div className="font-display font-bold text-lg mt-1">{value}</div>
    </div>
);

const Tile = ({ icon, label, value }) => (
    <div>
        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 inline-flex items-center gap-1.5">{icon} {label}</div>
        <div className="font-display font-extrabold text-3xl tracking-tightest mt-1">{value}</div>
    </div>
);

const KPI = ({ label, value, testId }) => (
    <div className="border hairline p-4" data-testid={testId}>
        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">{label}</div>
        <div className="font-display font-extrabold text-3xl md:text-4xl tracking-tightest mt-1.5">{value}</div>
    </div>
);

const CtaRow = ({ label, value, total, testId }) => {
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;
    return (
        <div data-testid={testId}>
            <div className="flex items-baseline justify-between mb-1">
                <span className="text-sm text-zinc-300">{label}</span>
                <span className="font-display font-bold text-base"><span className="text-white">{value.toLocaleString()}</span><span className="text-zinc-600 text-xs ml-2">{pct}%</span></span>
            </div>
            <div className="h-1 bg-white/[0.05]">
                <div className="h-full transition-all" style={{ width: `${pct}%`, background: "var(--tenant-primary)" }} />
            </div>
        </div>
    );
};

const OffersBox = ({ title, offers, tone }) => (
    <div className="border hairline p-4" data-testid={`offers-box-${tone}`}>
        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3">{title}</div>
        {offers.length === 0 ? (
            <div className="text-xs text-zinc-600 italic py-2">None</div>
        ) : (
            <ul className="space-y-2">
                {offers.map((o) => (
                    <li key={o.id} className="flex items-center justify-between text-sm border-t hairline pt-2 first:border-0 first:pt-0">
                        <span className="text-zinc-200 truncate pr-3">{o.title}</span>
                        <span className="font-mono text-xs text-zinc-400 shrink-0">{o.clicks.toLocaleString()} clicks</span>
                    </li>
                ))}
            </ul>
        )}
    </div>
);

const OfferModal = ({ initial, onClose, onSubmit }) => {
    const [draft, setDraft] = useState(initial);
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        try { setBusy(true); await onSubmit({ title: draft.title, active: !!draft.active }); }
        finally { setBusy(false); }
    };
    return (
        <Modal open onClose={onClose} testId="offer-modal" title={initial.id ? "Edit offer" : "New offer"}
            footer={<>
                <GhostButton onClick={onClose}>Cancel</GhostButton>
                <PrimaryButton tenantTheme onClick={submit} disabled={busy || !draft.title} data-testid="offer-submit">
                    {busy ? "Saving…" : initial.id ? "Save" : "Publish"}
                </PrimaryButton>
            </>}>
            <div className="grid grid-cols-12 gap-4">
                <Field label="Offer title" span={12} hint="Shown in the fan app">
                    <TextInput value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} placeholder="20% off boiler service for season-ticket holders" testId="offer-title" />
                </Field>
                <Field label="Live in fan app" span={12}>
                    <label className="inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} data-testid="offer-active" />
                        <span>{draft.active ? "Visible to fans" : "Hidden"}</span>
                    </label>
                </Field>
            </div>
        </Modal>
    );
};
